import { all, call, put, select, takeEvery } from 'redux-saga/effects'
import { getWebSocketToken } from 'rest/getWebSocketToken'
import appStorage from 'storage/appStorage'
import { RootState } from 'types/state'
import { ALLOW_ANONYMOUS_CHAT } from 'voice/AppStateManager'
import { authSelectors } from './auth'

export const CHECK_WS_CONNECTION = '@ws/CHECK_WS_CONNECTION'
export const ADD_SUSCRIBER = '@ws/ADD_SUSCRIBER'
export const REMOVE_SUSCRIBER = '@ws/REMOVE_SUSCRIBER'
export const BROADCAST_MESSAGE = '@ws/BROADCAST_MESSAGE'

// Internal
const PATCH = '@ws/PATCH'
const DISPATCH = '@ws/DISPATCH'
const ADD_SUSCRIBER_INNER = '@ws/ADD_SUSCRIBER_INNER'
const REMOVE_SUSCRIBER_INNER = '@ws/REMOVE_SUSCRIBER_INNER'
const SET_CONNECTION_ID = '@ws/SET_CONNECTION_ID'

export type WsState = 'idle' | 'connecting' | 'connected' | 'disconnected'

export type WsSubscriber<T> = (data: T, container: WsContainer<T>, msg: MessageEvent) => void

export type WsReducerType = {
  available: boolean
  state: WsState
  subscribers: WsSubscriber<unknown>[]
  connectionId: string
}
const initialState: WsReducerType = {
  available: false,
  state: 'idle',
  subscribers: [],
  connectionId: '',
}

export interface WsContainer<T> {
  org_id: number
  project_id: number
  user_id: number
  user_name: string
  arrived: number
  original_data: {
    session_id: number
    is_superuser: boolean
    data: T
  }
  type: 'broadcast'
}

const session_id = Math.round(Math.random() * 100000)
let ws: WebSocket | undefined
let wsOrgId: number | undefined
let wsUserId: number | undefined

export const wsActions = {
  checkConnection() {
    return { type: CHECK_WS_CONNECTION }
  },
  addSubscriber<T>(subscriber: WsSubscriber<T>): SubscribeType {
    return {
      type: ADD_SUSCRIBER,
      subscriber,
    }
  },
  removeSubscriber<T>(subscriber: WsSubscriber<T>): SubscribeType {
    return {
      type: REMOVE_SUSCRIBER,
      subscriber,
    }
  },
  broadcast(data: any): BroadcastMessageType {
    return {
      type: BROADCAST_MESSAGE,
      data,
    }
  },
}

interface PatchType {
  type: typeof PATCH
  patch: Partial<WsReducerType>
}
interface SubscribeType {
  type: typeof ADD_SUSCRIBER | typeof REMOVE_SUSCRIBER | typeof ADD_SUSCRIBER_INNER | typeof REMOVE_SUSCRIBER_INNER
  subscriber: WsSubscriber<any>
}
interface BroadcastMessageType {
  type: typeof BROADCAST_MESSAGE
  data: any
}
interface DispatchMessageType {
  type: typeof DISPATCH
  args: [unknown, WsContainer<unknown>, MessageEvent]
}

interface SetConnectionIdType {
  type: typeof SET_CONNECTION_ID
  payload: { connectionId: string }
}

type WsActionsType = SubscribeType | PatchType | BroadcastMessageType | DispatchMessageType | SetConnectionIdType

export default function wsReducer(state = initialState, action: WsActionsType): WsReducerType {
  switch (action.type) {
    case PATCH:
      return {
        ...state,
        ...action.patch,
      }
    case DISPATCH:
      setTimeout(() => {
        // Must call out after redux has finished
        for (const sub of state.subscribers) sub.apply(null, action.args)
      }, 0)
      return state
    case ADD_SUSCRIBER_INNER:
      return {
        ...state,
        subscribers: [...state.subscribers, action.subscriber],
      }
    case REMOVE_SUSCRIBER_INNER:
      return {
        ...state,
        subscribers: state.subscribers.filter((s) => s !== action.subscriber),
      }
    case SET_CONNECTION_ID:
      return {
        ...state,
        connectionId: action.payload.connectionId,
      }
    default:
      return state
  }
}

export const wsSelectors = {
  getAvailable: (state: RootState) => {
    return state.websocket.available
  },
  getState: (state: RootState) => {
    return state.websocket.state
  },
  getSubscribers: (state: RootState) => {
    return state.websocket.subscribers
  },
  getConnectionId: (state: RootState) => {
    return state.websocket.connectionId
  },
}

export function* wsSagas() {
  yield all([takeEvery(ADD_SUSCRIBER, addRemoveSubscriber)])
  yield all([takeEvery(REMOVE_SUSCRIBER, addRemoveSubscriber)])
  yield all([takeEvery(CHECK_WS_CONNECTION, checkSocketConnection)])
  yield all([takeEvery(BROADCAST_MESSAGE, broadcastMessage)])
}

// To get around redux async weirdness
let connecting = false
let connected = false
let reconnectAttempts = 0
let reconnectTimer: number | undefined
const INITIAL_RECONNECT_DELAY = 1000
const MAX_RECONNECT_DELAY = 30000
const LONG_TERM_RECONNECT_DELAY = 60000 // After initial attempts, retry every minute

const handleOffline = () => {
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.close(1000, 'Browser offline')
  }
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = undefined
  }
  reconnectAttempts = 0
  connecting = false
  connected = false
}

const handleOnline = () => {
  connecting = false
  connected = false

  if (!ws || ws.readyState !== WebSocket.OPEN) {
    window.reduxStore.dispatch(wsActions.checkConnection())
  }
}

function getReconnectDelay(attempts: number): number {
  if (attempts < 10) {
    // Exponential backoff for first 10 attempts: 1s, 2s, 4s, 8s, 16s, max 30s
    return Math.min(INITIAL_RECONNECT_DELAY * Math.pow(2, attempts), MAX_RECONNECT_DELAY) + Math.random() * 1000
  } else {
    // After 10 attempts, retry every minute indefinitely
    return LONG_TERM_RECONNECT_DELAY + Math.random() * 5000
  }
}

function reconnect() {
  window.reduxStore.dispatch({
    type: PATCH,
    patch: {
      available: false,
      state: ws ? 'disconnected' : 'idle',
    },
  })
  cleanupWebsocket()

  const delay = getReconnectDelay(reconnectAttempts)
  reconnectAttempts++

  reconnectTimer = window.setTimeout(() => {
    reconnectTimer = undefined
    console.log('Attempting to reconnect to Websocket in ', delay, 'ms, attempt: ', reconnectAttempts + 1)
    window.reduxStore.dispatch(wsActions.checkConnection())
  }, delay)
}

export function* addRemoveSubscriber(action: {
  type: typeof ADD_SUSCRIBER | typeof REMOVE_SUSCRIBER
  subscriber: WsSubscriber<any>
}) {
  const subscribers = yield select(wsSelectors.getSubscribers)
  let doCheck = false
  if (action.type === ADD_SUSCRIBER) {
    doCheck = subscribers.length === 0
    yield put({
      type: ADD_SUSCRIBER_INNER,
      subscriber: action.subscriber,
    })
  } else if (action.type === REMOVE_SUSCRIBER) {
    doCheck = subscribers.length === 1
    yield put({
      type: REMOVE_SUSCRIBER_INNER,
      subscriber: action.subscriber,
    })
  }

  if (doCheck) yield put(wsActions.checkConnection())
}

/**
 * Checks whether endpoint, org and token are available
 * Cleans up old
 */
export function* checkSocketConnection() {
  const orgId = yield select(authSelectors.getOrgId)
  const userId = yield select(authSelectors.getCurrentUserId)
  const subscribers = yield select(wsSelectors.getSubscribers)
  const isAnonymousShareUser = yield select(authSelectors.getIsAnonymous)
  const authToken = appStorage.getToken()

  if (!authToken && !isAnonymousShareUser) {
    console.warn('No authentication token available for websocket connection')
    return
  }

  if (window.WEBSOCKET_ENDPOINT && (!!userId || !!ALLOW_ANONYMOUS_CHAT) && subscribers.length) {
    if (wsUserId === userId && wsOrgId === orgId && (connected || connecting)) return
    connecting = true
    connected = false
    wsOrgId = orgId
    wsUserId = userId

    let token
    let authError = false
    try {
      token = yield getWebSocketToken(orgId)
    } catch (error: any) {
      console.warn('Failed to get websocket token:', error)
      token = null
      authError = error?.status === 401
      console.log('WebSocket token error:', { status: error?.status, message: error?.message, authError })
    }

    yield put({
      type: PATCH,
      patch: {
        available: !!token,
        state: token ? 'connecting' : 'idle',
      },
    })
    if (!token) {
      connecting = false
      yield call(cleanupWebsocket)
      if (!authError) {
        reconnect()
      } else {
        console.log('WebSocket authentication failed, not attempting reconnection')
      }
    } else {
      try {
        yield call(createWebsocket, token)
      } catch (error) {
        console.error('Failed to create websocket:', error)
        connecting = false
        yield call(cleanupWebsocket)
        yield put({
          type: PATCH,
          patch: {
            available: false,
            state: 'disconnected',
          },
        })
        reconnect()
      }
    }
  } else {
    connecting = false
    yield call(cleanupWebsocket)
    yield put({
      type: PATCH,
      patch: {
        available: false,
        state: 'idle',
      },
    })
  }
}
export function* broadcastMessage(action: BroadcastMessageType) {
  if (!ws || ws.readyState !== 1) {
    return
  }

  const projectId = yield select((state: RootState) => state.projectId)
  const is_superuser = yield select(authSelectors.getIsSuperUser)

  const msg = {
    action: 'broadcast',
    data: {
      session_id,
      is_superuser,
      data: action.data,
    },
    project_id: projectId,
  }

  ws.send(JSON.stringify(msg))
}

function cleanupWebsocket() {
  if (!ws) return
  wsOrgId = undefined
  wsUserId = undefined
  connecting = false
  connected = false
  ws.onopen = null
  ws.onmessage = null
  ws.onclose = null
  ws.onerror = null
  ws.close()
  ws = undefined
}

function createWebsocket(token: string) {
  return new Promise((resolve, reject) => {
    try {
      ws = new WebSocket(`${window.WEBSOCKET_ENDPOINT}?token=${token}`)
    } catch (error) {
      console.error('Failed to create WebSocket:', error)
      connecting = false
      reject(error)
      return
    }

    // Add global online/offline listeners - remove old ones first to handle hot reloads
    window.removeEventListener('offline', handleOffline)
    window.removeEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    window.addEventListener('online', handleOnline)

    ws.onopen = () => {
      // Reset reconnect attempts and connecting flag on successful connection
      reconnectAttempts = 0
      connecting = false
      connected = true

      // Cancel any pending reconnection timer since we're now connected
      if (reconnectTimer) {
        clearTimeout(reconnectTimer)
        reconnectTimer = undefined
      }

      console.log('WebSocket connected successfully')

      window.reduxStore.dispatch({
        type: PATCH,
        patch: {
          available: true,
          state: 'connected',
        },
      })

      // Request connection ID after connection is established
      if (ws && ws.readyState === WebSocket.OPEN) {
        const msg = {
          action: 'getConnectionId',
          data: {},
        }
        ws.send(JSON.stringify(msg))
      }
      resolve(ws)
    }

    ws.onmessage = (msg) => {
      let data
      try {
        data = JSON.parse(msg.data)
      } catch (e) {}
      if (!data) {
        console.warn('Failed to parse ws message data: ', msg)
        return
      }

      if (data.type === 'CONNECTION_ESTABLISHED') {
        window.reduxStore.dispatch({
          type: SET_CONNECTION_ID,
          payload: { connectionId: data.payload.connectionId },
        })
      } else if (data.type === 'WEBSOCKET_MESSAGE') {
        window.reduxStore.dispatch({
          type: data.payload.type,
          args: [data.payload.payload],
        })

        // @TODO: Refactor to somewhere more sensible, but for now keep it here to avoid creating a new location until we have a good architecture
        if (data?.payload?.type === 'task_completed' && data?.payload?.payload?.result_url) {
          window.SceneHelper.getAndApplyAsyncDesignData(data.payload.payload.result_url)
        }
      } else {
        if (!data.original_data) {
          console.warn('Error from websocket server: ', data)
          return
        }
        data.arrived = Date.now()
        if (data.original_data?.session_id === session_id) {
          // Message from self, ignore
          return
        }
        const user_data = data.original_data.data
        window.reduxStore.dispatch({
          type: DISPATCH,
          args: [user_data, data, msg],
        })
      }
    }

    ws.onclose = (e: CloseEvent) => {
      connecting = false
      reconnect()
    }

    ws.onerror = function (event: Event) {
      connecting = false
      reconnect()
    }
  })
}
